import { Box } from '@mui/material';
import { Fragment, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Panel, PanelGroup } from 'react-resizable-panels';
import BindPlaceholder from '../binding/BindPlaceholder';
import { LEFT_EDGE_PANE_STYLE, READOUT_DIM, READOUT_FONT } from '../chrome';
import { columnGroups, leadCellOf, panelCellsOf } from '../layout/layout';
import {
    setBandColumnWidths,
    setBandHeight,
    setColumnWidths,
    setSubRowHeights,
} from '../layout/mutations';
import { DatasetRow, PRIMARY_ROW_INDEX, ViewportCell } from '../layout/types';
import {
    tileAlignmentOf,
    tileGeometryLinkOf,
    tileStructureSourceOf,
} from '../linking/rowLock';
import DraggablePanelCell from './DraggablePanelCell';
import DraggableTileCell from './DraggableTileCell';
import GridRowFooter from './GridRowFooter';
import ResizeGutter from './ResizeGutter';
import PrimaryRowNotice from './PrimaryRowNotice';
import { NO_PRIMARY_IMAGE_NOTICE_ID } from './primaryRowNoticeId';
import { RowHost } from './RowHost';

/** Stands in for a pane whose image or plane the layout cannot resolve. */
function EmptyPane() {
    return (
        <Box
            sx={{
                height: '100%',
                bgcolor: 'common.black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: READOUT_DIM,
                fontFamily: READOUT_FONT,
                fontSize: 11,
            }}
        />
    );
}

/**
 * The one pane of an unbound row, activated by the pointer entering it or
 * focus reaching it, as a tile of a bound row is.
 */
function UnboundRowPane({
    rowIndex,
    host,
    children,
}: {
    rowIndex: number;
    host: RowHost;
    children: ReactNode;
}) {
    return (
        <Box
            onMouseEnter={() => host.onActivateUnboundRow(rowIndex)}
            onFocus={() => host.onActivateUnboundRow(rowIndex)}
            sx={{ height: '100%' }}
        >
            {children}
        </Box>
    );
}

interface GridRowProps {
    row: DatasetRow;
    rowIndex: number;
    host: RowHost;
}

/**
 * One dataset row: its image panes laid out in the row's tracks as
 * resizable columns, each column stacking the panes that share it; under
 * them the band of panels, when the row hangs one, as resizable columns of
 * its own behind a gutter that sets the band's height; and under both the
 * row's footer. A row is one dataset, so its window/level and identity
 * live in that single footer, and the blend stays row-scoped. The row's
 * first image pane carries the row's plane control. A panel pane
 * draws the panel the host supplies for its kind in a tile of its own. The
 * widgets the row hosts float over its panes, the band included. An
 * unbound row draws the bind placeholder instead, as one pane that the
 * pointer or focus activates.
 */
export default function GridRow({ row, rowIndex, host }: GridRowProps) {
    const intl = useIntl();
    // The primary row shows the primary image: what is being worked on. It
    // stays, and changes its image only by asking for another primary image.
    const isPrimary = rowIndex === PRIMARY_ROW_INDEX;
    const canClose = host.canCloseRow && !isPrimary;
    const notice = isPrimary ? host.primaryRowNotice : undefined;
    // An empty primary row says why it is empty, above a picker that
    // chooses the primary image, not the row's image, so the row never shows
    // an image that is not the primary image.
    if (!row.dataset && isPrimary) {
        return (
            <UnboundRowPane rowIndex={rowIndex} host={host}>
                <BindPlaceholder
                    availableDatasets={host.binding.availableDatasets}
                    onBind={host.primaryImage.choose}
                    notice={
                        notice ??
                        intl.formatMessage({ id: NO_PRIMARY_IMAGE_NOTICE_ID })
                    }
                />
            </UnboundRowPane>
        );
    }
    if (!row.dataset) {
        return (
            <UnboundRowPane rowIndex={rowIndex} host={host}>
                <BindPlaceholder
                    availableDatasets={host.binding.availableDatasets}
                    onBind={(uid) => host.binding.requestBind(rowIndex, uid)}
                    onClose={() => host.onCloseRow(rowIndex)}
                    canClose={canClose}
                />
            </UnboundRowPane>
        );
    }
    const volume = host.datasetAdapter.resolve(row.dataset.seriesInstanceUid);
    const lead = leadCellOf(row);
    const rowBlend = host.rowBlendFor(rowIndex);
    const lockState = host.rowLockFor(rowIndex);
    const alignment = tileAlignmentOf(lockState, host.primarySeriesUid);
    const geometryLink = tileGeometryLinkOf(lockState, rowIndex);
    const structuresThrough = tileStructureSourceOf(
        lockState,
        host.primarySeriesUid
    );

    const renderPane = (cell: ViewportCell): ReactNode => {
        const columnIndex = row.cells.indexOf(cell);
        if (cell.content.kind !== 'plane') {
            const panel = host.panelFor(cell.content.kind);
            return panel ? (
                <DraggablePanelCell
                    cell={cell}
                    panel={panel}
                    rowIndex={rowIndex}
                    columnIndex={columnIndex}
                    host={host}
                />
            ) : (
                <EmptyPane />
            );
        }
        if (!volume || !rowBlend) {
            return <EmptyPane />;
        }
        return (
            <DraggableTileCell
                cell={cell}
                plane={cell.content.plane}
                layer={cell.content.layer}
                volume={volume}
                alignment={alignment}
                structuresThrough={structuresThrough}
                geometryLink={geometryLink}
                blend={rowBlend}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                leadsRow={cell.id === lead?.id}
                earlierLayers={row.cells
                    .slice(0, columnIndex)
                    .flatMap(({ content }) =>
                        content.kind === 'plane' ? [content.layer] : []
                    )}
                planeCycle={
                    cell.id === lead?.id
                        ? host.planeCycleFor(cell.id, rowIndex)
                        : undefined
                }
                host={host}
            />
        );
    };

    const band = row.band;
    const panels = panelCellsOf(row);
    // The image panes and the band are the two panels of one vertical
    // group, so the image panes stay mounted when the band comes and goes.
    const imagesPanelId = `${lead?.id ?? rowIndex}-images`;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {notice ? <PrimaryRowNotice text={notice} /> : null}
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <PanelGroup
                    direction="vertical"
                    onLayout={(sizes) => {
                        if (sizes.length === 2) {
                            host.onLayoutChange((previous) =>
                                setBandHeight(previous, rowIndex, sizes[1])
                            );
                        }
                    }}
                    style={{ height: '100%' }}
                >
                    <Panel
                        id={imagesPanelId}
                        defaultSize={band ? 100 - band.heightPercent : 100}
                        minSize={15}
                        order={0}
                    >
                        <PanelGroup
                            direction="horizontal"
                            onLayout={(sizes) =>
                                host.onLayoutChange((previous) =>
                                    setColumnWidths(previous, rowIndex, sizes)
                                )
                            }
                            style={{ height: '100%' }}
                        >
                            {columnGroups(row).map((group, colIndex) => {
                                const [first] = group.cells;
                                return (
                                    <Fragment key={first.id}>
                                        {colIndex > 0 ? (
                                            <ResizeGutter direction="horizontal" />
                                        ) : null}
                                        <Panel
                                            id={first.id}
                                            defaultSize={
                                                row.columnPercents[colIndex]
                                            }
                                            minSize={8}
                                            order={colIndex}
                                            style={
                                                colIndex === 0
                                                    ? LEFT_EDGE_PANE_STYLE
                                                    : undefined
                                            }
                                        >
                                            {group.cells.length === 1 ? (
                                                renderPane(first)
                                            ) : (
                                                <PanelGroup
                                                    direction="vertical"
                                                    onLayout={(sizes) =>
                                                        host.onLayoutChange(
                                                            (previous) =>
                                                                setSubRowHeights(
                                                                    previous,
                                                                    rowIndex,
                                                                    sizes
                                                                )
                                                        )
                                                    }
                                                    style={{ height: '100%' }}
                                                >
                                                    {group.cells.map(
                                                        (cell, subIndex) => (
                                                            <Fragment
                                                                key={cell.id}
                                                            >
                                                                {subIndex >
                                                                0 ? (
                                                                    <ResizeGutter direction="vertical" />
                                                                ) : null}
                                                                <Panel
                                                                    id={cell.id}
                                                                    defaultSize={
                                                                        row
                                                                            .rowPercents[
                                                                            subIndex
                                                                        ]
                                                                    }
                                                                    minSize={8}
                                                                    order={
                                                                        subIndex
                                                                    }
                                                                >
                                                                    {renderPane(
                                                                        cell
                                                                    )}
                                                                </Panel>
                                                            </Fragment>
                                                        )
                                                    )}
                                                </PanelGroup>
                                            )}
                                        </Panel>
                                    </Fragment>
                                );
                            })}
                        </PanelGroup>
                    </Panel>
                    {band && panels.length > 0 ? (
                        <>
                            <ResizeGutter direction="vertical" />
                            <Panel
                                id={`${imagesPanelId}-band`}
                                defaultSize={band.heightPercent}
                                minSize={10}
                                order={1}
                            >
                                <PanelGroup
                                    direction="horizontal"
                                    onLayout={(sizes) =>
                                        host.onLayoutChange((previous) =>
                                            setBandColumnWidths(
                                                previous,
                                                rowIndex,
                                                sizes
                                            )
                                        )
                                    }
                                    style={{ height: '100%' }}
                                >
                                    {panels.map((cell, index) => (
                                        <Fragment key={cell.id}>
                                            {index > 0 ? (
                                                <ResizeGutter direction="horizontal" />
                                            ) : null}
                                            <Panel
                                                id={cell.id}
                                                defaultSize={
                                                    band.columnPercents[index]
                                                }
                                                minSize={8}
                                                order={index}
                                                style={
                                                    index === 0
                                                        ? LEFT_EDGE_PANE_STYLE
                                                        : undefined
                                                }
                                            >
                                                {renderPane(cell)}
                                            </Panel>
                                        </Fragment>
                                    ))}
                                </PanelGroup>
                            </Panel>
                        </>
                    ) : null}
                </PanelGroup>
                {host.widgetLayerFor(rowIndex)}
            </Box>
            <GridRowFooter row={row} rowIndex={rowIndex} host={host} />
        </Box>
    );
}
